import React, { useState } from "react";

type SettingsTab =
  | "academy"
  | "security"
  | "notifications"
  | "payment"
  | "appearance";

type ToggleKey =
  | "studentSignup"
  | "teacherApproval"
  | "twoFactorLogin"
  | "autoBackup"
  | "emailNotifications"
  | "smsNotifications"
  | "whatsappAlerts"
  | "paymentReminder"
  | "autoInvoice"
  | "maintenanceMode"
  | "darkAdmin";

const AdminSettings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<SettingsTab>("academy");

  const [academy, setAcademy] = useState({
    name: "Mediit Academy",
    email: "admin@mediitacademy.com",
    phone: "+91 98765 43210",
    website: "https://mediitacademy.com",
    address: "Patna, Bihar, India",
    tagline: "Learn. Practice. Achieve.",
    supportEmail: "support@mediitacademy.com",
  });

  const [payment, setPayment] = useState({
    currency: "INR",
    tax: "18",
    gateway: "Razorpay",
    upiId: "mediitacademy@upi",
    bankName: "HDFC Bank",
    accountNumber: "1234567890",
    ifsc: "HDFC0001234",
  });

  const [toggles, setToggles] = useState<Record<ToggleKey, boolean>>({
    studentSignup: true,
    teacherApproval: true,
    twoFactorLogin: false,
    autoBackup: true,
    emailNotifications: true,
    smsNotifications: false,
    whatsappAlerts: true,
    paymentReminder: true,
    autoInvoice: true,
    maintenanceMode: false,
    darkAdmin: false,
  });

  const [roles, setRoles] = useState([
    {
      name: "Super Admin",
      users: 2,
      access: "Full Access",
      color: "bg-slate-950 text-white",
    },
    {
      name: "Teacher",
      users: 18,
      access: "Courses, Tests, Live Classes",
      color: "bg-blue-50 text-blue-700",
    },
    {
      name: "Counsellor",
      users: 6,
      access: "Students, Payments, Reports",
      color: "bg-emerald-50 text-emerald-700",
    },
    {
      name: "Accountant",
      users: 3,
      access: "Payments, Invoices, Refunds",
      color: "bg-orange-50 text-orange-700",
    },
  ]);

  const updateToggle = (key: ToggleKey) => {
    setToggles((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const saveSettings = () => {
    alert("Settings saved successfully. Backend connect hone ke baad ye database me save hoga.");
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <section className="overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-sm">
        <div className="grid gap-0 xl:grid-cols-[1.15fr_0.85fr]">
          <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-6 text-white sm:p-8">
            <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-cyan-400/20 blur-3xl" />
            <div className="absolute -bottom-28 left-28 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl" />

            <div className="relative">
              <div className="mb-5 inline-flex rounded-full border border-white/10 bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-wide text-cyan-100">
                Admin Settings
              </div>

              <h1 className="max-w-3xl text-3xl font-black tracking-tight sm:text-4xl xl:text-5xl">
                Control your LMS configuration from one place.
              </h1>

              <p className="mt-4 max-w-2xl text-sm font-semibold leading-6 text-slate-300 sm:text-base">
                Academy profile, security, notifications, payment gateway,
                roles, permissions aur appearance settings yaha se manage karo.
              </p>

              <div className="mt-7 flex flex-wrap gap-3">
                <button
                  onClick={saveSettings}
                  className="rounded-2xl bg-white px-5 py-3 text-sm font-black text-slate-950 hover:bg-cyan-50"
                >
                  Save All Settings
                </button>
                <button className="rounded-2xl border border-white/15 bg-white/10 px-5 py-3 text-sm font-black text-white hover:bg-white/15">
                  Reset Defaults
                </button>
              </div>
            </div>
          </div>

          <div className="bg-slate-50 p-6 sm:p-8">
            <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-gradient-to-br from-blue-600 to-cyan-500 text-2xl font-black text-white shadow-lg shadow-blue-600/20">
                  M
                </div>
                <div>
                  <h2 className="text-2xl font-black text-slate-950">
                    {academy.name}
                  </h2>
                  <p className="mt-1 text-sm font-bold text-slate-500">
                    LMS Management System
                  </p>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <InfoMini title="Active Users" value="12,450" />
                <InfoMini title="Admin Roles" value="04" />
                <InfoMini title="Backup" value="Enabled" />
                <InfoMini title="Security" value="Good" />
              </div>

              <div className="mt-5 rounded-2xl bg-emerald-50 p-4">
                <p className="text-xs font-black uppercase tracking-wide text-emerald-700">
                  System Health
                </p>
                <div className="mt-3 h-2 rounded-full bg-emerald-100">
                  <div className="h-2 w-[92%] rounded-full bg-emerald-500" />
                </div>
                <p className="mt-2 text-xs font-bold text-emerald-700">
                  92% optimized and secure
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="flex flex-wrap gap-2">
          {[
            { label: "Academy Profile", value: "academy" },
            { label: "Security", value: "security" },
            { label: "Notifications", value: "notifications" },
            { label: "Payment", value: "payment" },
            { label: "Appearance", value: "appearance" },
          ].map((tab) => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value as SettingsTab)}
              className={`rounded-2xl px-4 py-2.5 text-sm font-black transition ${
                activeTab === tab.value
                  ? "bg-slate-950 text-white shadow-lg shadow-slate-950/10"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </section>

      {activeTab === "academy" && (
        <section className="grid gap-6 xl:grid-cols-[1fr_360px]">
          <div className="space-y-6">
            <SettingsCard
              title="Academy Information"
              subtitle="Public profile and contact details"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <FormInput
                  label="Academy Name"
                  value={academy.name}
                  onChange={(value) => setAcademy({ ...academy, name: value })}
                />
                <FormInput
                  label="Tagline"
                  value={academy.tagline}
                  onChange={(value) => setAcademy({ ...academy, tagline: value })}
                />
                <FormInput
                  label="Admin Email"
                  value={academy.email}
                  onChange={(value) => setAcademy({ ...academy, email: value })}
                />
                <FormInput
                  label="Support Email"
                  value={academy.supportEmail}
                  onChange={(value) =>
                    setAcademy({ ...academy, supportEmail: value })
                  }
                />
                <FormInput
                  label="Phone"
                  value={academy.phone}
                  onChange={(value) => setAcademy({ ...academy, phone: value })}
                />
                <FormInput
                  label="Website"
                  value={academy.website}
                  onChange={(value) => setAcademy({ ...academy, website: value })}
                />
                <div className="sm:col-span-2">
                  <FormInput
                    label="Address"
                    value={academy.address}
                    onChange={(value) =>
                      setAcademy({ ...academy, address: value })
                    }
                  />
                </div>
              </div>
            </SettingsCard>

            <SettingsCard title="Access Roles" subtitle="Admin side permissions">
              <div className="grid gap-4 lg:grid-cols-2">
                {roles.map((role) => (
                  <div
                    key={role.name}
                    className="rounded-[24px] border border-slate-100 bg-slate-50 p-4"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-black ${role.color}`}
                        >
                          {role.name}
                        </span>
                        <p className="mt-3 text-sm font-black text-slate-950">
                          {role.access}
                        </p>
                        <p className="mt-1 text-xs font-bold text-slate-500">
                          {role.users} users assigned
                        </p>
                      </div>
                      <button className="rounded-xl bg-white px-3 py-2 text-xs font-black text-slate-700 hover:bg-slate-100">
                        Edit
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </SettingsCard>
          </div>

          <SettingsSide />
        </section>
      )}

      {activeTab === "security" && (
        <section className="grid gap-6 xl:grid-cols-[1fr_360px]">
          <div className="space-y-6">
            <SettingsCard title="Login & Security" subtitle="Protect admin and staff accounts">
              <div className="grid gap-4">
                <ToggleRow
                  title="Two Factor Login"
                  description="Admin login ke time OTP verification enable hoga."
                  checked={toggles.twoFactorLogin}
                  onChange={() => updateToggle("twoFactorLogin")}
                />
                <ToggleRow
                  title="Teacher Approval Required"
                  description="New teacher account admin approval ke baad active hoga."
                  checked={toggles.teacherApproval}
                  onChange={() => updateToggle("teacherApproval")}
                />
                <ToggleRow
                  title="Auto Backup"
                  description="Daily data backup automatically create hoga."
                  checked={toggles.autoBackup}
                  onChange={() => updateToggle("autoBackup")}
                />
                <ToggleRow
                  title="Maintenance Mode"
                  description="Enable karne par students ko maintenance page dikhega."
                  checked={toggles.maintenanceMode}
                  onChange={() => updateToggle("maintenanceMode")}
                />
              </div>
            </SettingsCard>

            <SettingsCard title="Password Policy" subtitle="Admin and staff password rules">
              <div className="grid gap-4 sm:grid-cols-2">
                <SelectInput label="Minimum Password Length" options={["8", "10", "12", "16"]} />
                <SelectInput label="Password Expiry" options={["Never", "30 Days", "60 Days", "90 Days"]} />
                <SelectInput label="Login Attempts" options={["3 Attempts", "5 Attempts", "10 Attempts"]} />
                <SelectInput label="Session Timeout" options={["15 Minutes", "30 Minutes", "1 Hour", "2 Hours"]} />
              </div>
            </SettingsCard>
          </div>

          <SecurityPanel />
        </section>
      )}

      {activeTab === "notifications" && (
        <section className="grid gap-6 xl:grid-cols-[1fr_360px]">
          <div className="space-y-6">
            <SettingsCard title="Notification Channels" subtitle="Student, teacher and admin alerts">
              <div className="grid gap-4">
                <ToggleRow
                  title="Email Notifications"
                  description="Admission, payment, test and class updates email par jayenge."
                  checked={toggles.emailNotifications}
                  onChange={() => updateToggle("emailNotifications")}
                />
                <ToggleRow
                  title="SMS Notifications"
                  description="Important OTP and reminders SMS se send honge."
                  checked={toggles.smsNotifications}
                  onChange={() => updateToggle("smsNotifications")}
                />
                <ToggleRow
                  title="WhatsApp Alerts"
                  description="Class reminders and payment follow-up WhatsApp par jayega."
                  checked={toggles.whatsappAlerts}
                  onChange={() => updateToggle("whatsappAlerts")}
                />
                <ToggleRow
                  title="Payment Reminder"
                  description="Pending fees ke liye auto reminder send hoga."
                  checked={toggles.paymentReminder}
                  onChange={() => updateToggle("paymentReminder")}
                />
              </div>
            </SettingsCard>

            <SettingsCard title="Message Templates" subtitle="Reusable notification messages">
              <div className="grid gap-4">
                <TemplateBox title="Class Reminder" text="Dear student, your live class will start in 30 minutes. Join from your dashboard." />
                <TemplateBox title="Payment Pending" text="Your course fee is pending. Please complete payment to continue learning." />
                <TemplateBox title="Test Published" text="New mock test is live. Attempt now and check your rank." />
              </div>
            </SettingsCard>
          </div>

          <NotificationPanel />
        </section>
      )}

      {activeTab === "payment" && (
        <section className="grid gap-6 xl:grid-cols-[1fr_360px]">
          <div className="space-y-6">
            <SettingsCard title="Payment Gateway" subtitle="Fees collection and invoice settings">
              <div className="grid gap-4 sm:grid-cols-2">
                <SelectInput
                  label="Currency"
                  value={payment.currency}
                  options={["INR", "USD", "AED"]}
                  onChange={(value) => setPayment({ ...payment, currency: value })}
                />
                <SelectInput
                  label="Gateway"
                  value={payment.gateway}
                  options={["Razorpay", "Cashfree", "PhonePe", "Manual"]}
                  onChange={(value) => setPayment({ ...payment, gateway: value })}
                />
                <FormInput
                  label="Tax Percentage"
                  value={payment.tax}
                  onChange={(value) => setPayment({ ...payment, tax: value })}
                />
                <FormInput
                  label="UPI ID"
                  value={payment.upiId}
                  onChange={(value) => setPayment({ ...payment, upiId: value })}
                />
              </div>
            </SettingsCard>

            <SettingsCard title="Bank Details" subtitle="Manual payment and settlement account">
              <div className="grid gap-4 sm:grid-cols-2">
                <FormInput
                  label="Bank Name"
                  value={payment.bankName}
                  onChange={(value) => setPayment({ ...payment, bankName: value })}
                />
                <FormInput
                  label="Account Number"
                  value={payment.accountNumber}
                  onChange={(value) =>
                    setPayment({ ...payment, accountNumber: value })
                  }
                />
                <FormInput
                  label="IFSC Code"
                  value={payment.ifsc}
                  onChange={(value) => setPayment({ ...payment, ifsc: value })}
                />
                <div className="rounded-2xl bg-emerald-50 p-4">
                  <p className="text-xs font-black uppercase tracking-wide text-emerald-700">
                    Settlement
                  </p>
                  <p className="mt-2 text-sm font-bold text-emerald-700">
                    Auto settlement enabled every 24 hours.
                  </p>
                </div>
              </div>
            </SettingsCard>
          </div>

          <aside className="space-y-6">
            <SettingsCard title="Invoice Settings" subtitle="Receipt and billing">
              <div className="grid gap-4">
                <ToggleRow
                  title="Auto Invoice"
                  description="Payment complete hote hi invoice generate hoga."
                  checked={toggles.autoInvoice}
                  onChange={() => updateToggle("autoInvoice")}
                />
                <SelectInput label="Invoice Prefix" options={["MED-INV", "LMS-INV", "ACADEMY-INV"]} />
                <SelectInput label="Receipt Format" options={["PDF + Email", "PDF Only", "Email Only"]} />
              </div>
            </SettingsCard>

            <GatewayStatus />
          </aside>
        </section>
      )}

      {activeTab === "appearance" && (
        <section className="grid gap-6 xl:grid-cols-[1fr_360px]">
          <div className="space-y-6">
            <SettingsCard title="Brand Appearance" subtitle="Admin panel and student panel look">
              <div className="grid gap-4 sm:grid-cols-2">
                <SelectInput label="Primary Color" options={["Blue", "Cyan", "Emerald", "Violet", "Orange"]} />
                <SelectInput label="Font Family" options={["Inter", "Poppins", "Roboto", "System Default"]} />
                <SelectInput label="Card Radius" options={["Soft", "Medium", "Rounded", "Extra Rounded"]} />
                <SelectInput label="Layout Density" options={["Comfortable", "Compact", "Spacious"]} />
              </div>
            </SettingsCard>

            <SettingsCard title="Theme Options" subtitle="Panel behavior and mode">
              <div className="grid gap-4">
                <ToggleRow
                  title="Dark Admin Mode"
                  description="Admin dashboard ke liye dark theme enable hoga."
                  checked={toggles.darkAdmin}
                  onChange={() => updateToggle("darkAdmin")}
                />
                <ToggleRow
                  title="Student Signup"
                  description="Students public website se signup kar sakenge."
                  checked={toggles.studentSignup}
                  onChange={() => updateToggle("studentSignup")}
                />
              </div>
            </SettingsCard>
          </div>

          <AppearancePreview academyName={academy.name} />
        </section>
      )}

      <section className="sticky bottom-4 z-20 rounded-[28px] border border-slate-200 bg-white/95 p-4 shadow-xl backdrop-blur">
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <div>
            <p className="text-sm font-black text-slate-950">
              Unsaved changes may exist
            </p>
            <p className="text-xs font-bold text-slate-500">
              Save settings before leaving this page.
            </p>
          </div>
          <div className="flex gap-3">
            <button className="rounded-2xl bg-slate-100 px-5 py-3 text-sm font-black text-slate-700 hover:bg-slate-200">
              Cancel
            </button>
            <button
              onClick={saveSettings}
              className="rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white shadow-lg shadow-blue-600/20 hover:bg-blue-700"
            >
              Save Settings
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

const SettingsCard = ({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) => {
  return (
    <div className="rounded-[30px] border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
      <div className="mb-5">
        <h2 className="text-xl font-black text-slate-950">{title}</h2>
        <p className="mt-1 text-sm font-semibold text-slate-500">{subtitle}</p>
      </div>
      {children}
    </div>
  );
};

const FormInput = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) => {
  return (
    <div>
      <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">
        {label}
      </label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:bg-white"
      />
    </div>
  );
};

const SelectInput = ({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: string[];
  value?: string;
  onChange?: (value: string) => void;
}) => {
  return (
    <div>
      <label className="mb-2 block text-xs font-black uppercase tracking-wide text-slate-500">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700 outline-none focus:border-blue-500 focus:bg-white"
      >
        {options.map((item) => (
          <option key={item}>{item}</option>
        ))}
      </select>
    </div>
  );
};

const ToggleRow = ({
  title,
  description,
  checked,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  onChange: () => void;
}) => {
  return (
    <button
      type="button"
      onClick={onChange}
      className="flex w-full items-center justify-between gap-4 rounded-2xl border border-slate-100 bg-slate-50 p-4 text-left hover:bg-slate-100"
    >
      <div>
        <p className="text-sm font-black text-slate-950">{title}</p>
        <p className="mt-1 text-xs font-bold text-slate-400">{description}</p>
      </div>

      <span
        className={`flex h-7 w-12 shrink-0 items-center rounded-full p-1 transition ${
          checked ? "bg-blue-600" : "bg-slate-300"
        }`}
      >
        <span
          className={`h-5 w-5 rounded-full bg-white transition ${
            checked ? "translate-x-5" : "translate-x-0"
          }`}
        />
      </span>
    </button>
  );
};

const InfoMini = ({ title, value }: { title: string; value: string }) => (
  <div className="rounded-2xl bg-slate-50 p-4">
    <p className="text-xs font-bold text-slate-400">{title}</p>
    <p className="mt-1 text-lg font-black text-slate-950">{value}</p>
  </div>
);

const SettingsSide = () => (
  <aside className="space-y-6">
    <SettingsCard title="Setup Progress" subtitle="LMS configuration checklist">
      <div className="space-y-4">
        <ProgressItem label="Academy Profile" value="100%" />
        <ProgressItem label="Payment Setup" value="82%" />
        <ProgressItem label="Security Setup" value="74%" />
        <ProgressItem label="Notification Setup" value="68%" />
      </div>
    </SettingsCard>

    <SettingsCard title="Quick Actions" subtitle="Common setting tasks">
      <div className="grid gap-3">
        {[
          "Upload Logo",
          "Create New Role",
          "Invite Admin User",
          "Backup Now",
          "Clear Cache",
        ].map((item) => (
          <button
            key={item}
            className="group flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-left text-sm font-black text-slate-700 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
          >
            {item}
            <span className="transition group-hover:translate-x-1">→</span>
          </button>
        ))}
      </div>
    </SettingsCard>
  </aside>
);

const SecurityPanel = () => (
  <aside className="space-y-6">
    <SettingsCard title="Security Score" subtitle="Current admin protection">
      <div className="flex justify-center">
        <div className="relative flex h-44 w-44 items-center justify-center rounded-full bg-blue-50">
          <div className="flex h-32 w-32 flex-col items-center justify-center rounded-full bg-white shadow-sm">
            <p className="text-4xl font-black text-blue-600">86</p>
            <p className="text-xs font-bold text-slate-400">Score</p>
          </div>
        </div>
      </div>
      <p className="mt-4 text-center text-sm font-bold text-slate-500">
        Strong security. Enable 2FA to improve more.
      </p>
    </SettingsCard>

    <SettingsCard title="Recent Login Activity" subtitle="Admin access log">
      <div className="space-y-4">
        <ActivityItem title="Admin logged in" time="10 min ago" />
        <ActivityItem title="Teacher role updated" time="1 hour ago" />
        <ActivityItem title="Backup completed" time="2 hours ago" />
      </div>
    </SettingsCard>
  </aside>
);

const NotificationPanel = () => (
  <aside className="space-y-6">
    <SettingsCard title="Delivery Status" subtitle="Last 24 hours">
      <div className="grid gap-3">
        <InfoMini title="Emails Sent" value="1,240" />
        <InfoMini title="WhatsApp Sent" value="860" />
        <InfoMini title="SMS Sent" value="420" />
        <InfoMini title="Failed" value="18" />
      </div>
    </SettingsCard>

    <SettingsCard title="Provider Setup" subtitle="Connected services">
      <div className="space-y-3">
        <ProviderRow name="SMTP Email" status="Connected" />
        <ProviderRow name="WhatsApp API" status="Connected" />
        <ProviderRow name="SMS Gateway" status="Pending" />
      </div>
    </SettingsCard>
  </aside>
);

const GatewayStatus = () => (
  <SettingsCard title="Gateway Status" subtitle="Payment provider health">
    <div className="space-y-4">
      <ProviderRow name="Razorpay" status="Connected" />
      <ProviderRow name="UPI Collection" status="Active" />
      <ProviderRow name="Auto Refund" status="Manual" />
      <ProviderRow name="Settlement" status="Enabled" />
    </div>
  </SettingsCard>
);

const AppearancePreview = ({ academyName }: { academyName: string }) => (
  <aside className="space-y-6">
    <SettingsCard title="Live Preview" subtitle="Admin panel style preview">
      <div className="overflow-hidden rounded-[26px] border border-slate-200 bg-slate-50">
        <div className="bg-slate-950 p-4 text-white">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-600 text-sm font-black">
              M
            </div>
            <div>
              <p className="text-sm font-black">{academyName}</p>
              <p className="text-xs font-bold text-slate-400">Admin Panel</p>
            </div>
          </div>
        </div>
        <div className="p-4">
          <div className="rounded-2xl bg-white p-4 shadow-sm">
            <p className="text-xs font-bold text-slate-400">Dashboard Card</p>
            <p className="mt-1 text-2xl font-black text-slate-950">₹84.2L</p>
            <div className="mt-3 h-2 rounded-full bg-slate-100">
              <div className="h-2 w-[72%] rounded-full bg-blue-600" />
            </div>
          </div>
        </div>
      </div>
    </SettingsCard>

    <SettingsCard title="Brand Assets" subtitle="Logo and favicon">
      <div className="grid gap-3">
        <button className="rounded-2xl bg-slate-100 px-4 py-3 text-sm font-black text-slate-700 hover:bg-slate-200">
          Upload Logo
        </button>
        <button className="rounded-2xl bg-slate-100 px-4 py-3 text-sm font-black text-slate-700 hover:bg-slate-200">
          Upload Favicon
        </button>
      </div>
    </SettingsCard>
  </aside>
);

const ProgressItem = ({ label, value }: { label: string; value: string }) => (
  <div>
    <div className="mb-2 flex justify-between text-sm font-black text-slate-700">
      <span>{label}</span>
      <span>{value}</span>
    </div>
    <div className="h-2 rounded-full bg-slate-100">
      <div className="h-2 rounded-full bg-blue-600" style={{ width: value }} />
    </div>
  </div>
);

const TemplateBox = ({ title, text }: { title: string; text: string }) => (
  <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-sm font-black text-slate-950">{title}</p>
        <p className="mt-1 text-sm font-semibold leading-6 text-slate-500">
          {text}
        </p>
      </div>
      <button className="rounded-xl bg-white px-3 py-2 text-xs font-black text-slate-700 hover:bg-slate-100">
        Edit
      </button>
    </div>
  </div>
);

const ActivityItem = ({ title, time }: { title: string; time: string }) => (
  <div className="flex gap-3 rounded-2xl bg-slate-50 p-4">
    <div className="mt-1 h-2.5 w-2.5 rounded-full bg-blue-600" />
    <div>
      <p className="text-sm font-black text-slate-800">{title}</p>
      <p className="mt-1 text-xs font-bold text-slate-400">{time}</p>
    </div>
  </div>
);

const ProviderRow = ({ name, status }: { name: string; status: string }) => (
  <div className="flex items-center justify-between rounded-2xl bg-slate-50 p-4">
    <p className="text-sm font-black text-slate-800">{name}</p>
    <span
      className={`rounded-full px-3 py-1 text-xs font-black ${
        status === "Connected" || status === "Active" || status === "Enabled"
          ? "bg-emerald-50 text-emerald-700"
          : status === "Pending"
          ? "bg-orange-50 text-orange-700"
          : "bg-blue-50 text-blue-700"
      }`}
    >
      {status}
    </span>
  </div>
);

export default AdminSettings;